import * as React from "react";
import { View, Text, Pressable, useWindowDimensions } from "react-native";
import { Dialog, Portal } from "react-native-paper";
import THEME from "../../config/theme";
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { hexToRgba } from '../../utils/colorUtils';

/**
 * Caixa de alerta/confirmação usada no app inteiro.
 *
 * Paleta:
 *
 * - PADRÃO → cores e fontes da MARCA DO CLIENTE. Dentro do app usa o tema
 *   `app`; nas telas antes do login (Login, Cadastro, página de vendas) usa o
 *   tema `auth`, que é o que aquelas telas já usam ao redor. O escopo é
 *   detectado pela sessão, e `scope` ('app' | 'auth') força quando preciso.
 * - `variant="admin"` → cores e fonte neutras do admin (ADMIN_COLORS/
 *   ADMIN_FONT), iguais para todos os clientes.
 *
 * O padrão é o cliente DE PROPÓSITO: telas compartilhadas entre admin e aluno
 * (Comunidade, produto, cadastro, página de vendas, perfil) devem sempre sair
 * na marca do cliente. Só quem é exclusivo do painel pede `variant="admin"` —
 * assim uma tela nova nasce com a cor certa sem ninguém precisar lembrar.
 *
 * Props: visible, onClose, title, message, leftButton, leftButtonFunction,
 * rightButton, rightButtonFunction.
 * `leftButton` é a ação principal (OK/Sim) e `rightButton` a secundária (Não).
 */
export function AlertBox(props) {
  const {
    visible,
    onClose,
    title,
    message,
    leftButton,
    leftButtonFunction,
    rightButton,
    rightButtonFunction,
    variant,
    scope,
  } = props;

  const { customColor, customFont } = useCustomTheme();
  const { isLogged } = useAuthUser();
  const { width } = useWindowDimensions();
  const isNarrow = width < 480;

  const isAdmin = variant === 'admin';
  // Sem sessão iniciada só existem telas de auth (login, cadastro, vendas).
  const isAuthScope = scope ? scope === 'auth' : !isLogged;

  const palette = React.useMemo(() => {
    if (isAdmin) {
      return {
        surface: ADMIN_COLORS.surface,
        border: ADMIN_COLORS.border,
        title: ADMIN_COLORS.ink,
        text: ADMIN_COLORS.muted,
        primaryBg: ADMIN_COLORS.accent,
        primaryBgHover: ADMIN_COLORS.accentDark,
        primaryText: '#FFFFFF',
        secondaryBg: ADMIN_COLORS.secondaryBg,
        secondaryBorder: 'transparent',
        secondaryText: ADMIN_COLORS.secondaryText,
        fontTitle: ADMIN_FONT,
        fontText: ADMIN_FONT,
      };
    }

    const brandSurface =
      (isAuthScope ? customColor?.background?.auth : customColor?.background?.app) ||
      THEME.COLORS.BACKGROUND_MAIN;
    const brandText =
      (isAuthScope ? customColor?.text?.auth : customColor?.text?.app) ||
      THEME.COLORS.TEXT_MAIN;
    const brandButtonBg =
      (isAuthScope
        ? customColor?.button?.backgroundAuth
        : customColor?.button?.backgroundApp) || THEME.COLORS.PRIMARY_900;
    const brandButtonText =
      (isAuthScope ? customColor?.button?.textAuth : customColor?.button?.textApp) ||
      THEME.COLORS.TEXT_BUTTON;

    return {
      surface: brandSurface,
      // Borda e texto secundário derivados da cor de texto da marca: funciona
      // igual em tema claro e escuro, sem precisar de uma paleta fixa.
      border: hexToRgba(brandText, 0.15) || '#E5E7EB',
      title: brandText,
      text: hexToRgba(brandText, 0.75) || brandText,
      primaryBg: brandButtonBg,
      primaryBgHover: brandButtonBg,
      primaryText: brandButtonText,
      secondaryBg: 'transparent',
      secondaryBorder: hexToRgba(brandText, 0.25) || '#E5E7EB',
      secondaryText: brandText,
      fontTitle: customFont?.fontFamilyTitle || THEME.FONTFAMILY.BOLD,
      fontText: customFont?.fontFamilyText || THEME.FONTFAMILY.REGULAR,
    };
  }, [isAdmin, isAuthScope, customColor, customFont]);

  // `onClose` é chamado com `false` porque vários call sites usam a própria
  // função de setState do `visible`.
  const close = () => onClose?.(false);

  const handlePrimary = () => {
    close();
    leftButtonFunction?.();
  };

  const handleSecondary = () => {
    close();
    rightButtonFunction?.();
  };

  const actionButton = ({ label, onPress, primary }) => (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={({ hovered, pressed }) => [
        {
          minHeight: 44,
          borderRadius: 10,
          paddingHorizontal: 22,
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: primary ? palette.primaryBg : palette.secondaryBg,
          borderWidth: 1,
          borderColor: primary ? palette.primaryBg : palette.secondaryBorder,
          ...(isNarrow ? { width: '100%' } : null),
        },
        hovered && primary && { backgroundColor: palette.primaryBgHover },
        (hovered || pressed) && !primary && { opacity: 0.75 },
      ]}
    >
      <Text
        style={{
          fontFamily: palette.fontText,
          fontSize: 14,
          fontWeight: '600',
          color: primary ? palette.primaryText : palette.secondaryText,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={close}
        style={{
          alignSelf: 'center',
          width: '100%',
          // Antes era `window.innerWidth * 0.7`: além de quebrar no app nativo
          // (não existe `window`), virava um diálogo enorme no desktop.
          maxWidth: 460,
          marginHorizontal: 20,
          borderRadius: 16,
          backgroundColor: palette.surface,
          borderWidth: 1,
          borderColor: palette.border,
          boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
        }}
      >
        {/* `marginTop: 0` de propósito: o Dialog do Paper v5 clona o PRIMEIRO
            filho injetando `marginTop: 24`, que somaria ao padding daqui e
            deixaria um vão no topo. O style local vem depois e vence. */}
        <View style={{ marginTop: 0, padding: 24 }}>
          {title ? (
            <Text
              style={{
                fontFamily: palette.fontTitle,
                fontSize: 18,
                fontWeight: '700',
                color: palette.title,
                lineHeight: 24,
                marginBottom: message ? 8 : 0,
              }}
            >
              {title}
            </Text>
          ) : null}

          {message ? (
            <Text
              style={{
                fontFamily: palette.fontText,
                fontSize: 14,
                color: palette.text,
                lineHeight: 20,
              }}
            >
              {message}
            </Text>
          ) : null}

          <View
            style={{
              flexDirection: isNarrow ? 'column-reverse' : 'row',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 24,
            }}
          >
            {/* Secundária primeiro (Não/Cancelar), principal por último — mesma
                ordem dos demais modais do painel. Antes cada botão ficava numa
                linha própria, um embaixo do outro. */}
            {rightButton
              ? actionButton({
                  label: rightButton,
                  onPress: handleSecondary,
                  primary: false,
                })
              : null}
            {actionButton({
              label: leftButton || 'OK',
              onPress: handlePrimary,
              primary: true,
            })}
          </View>
        </View>
      </Dialog>
    </Portal>
  );
}
